import { Box3, Vector3 } from "three";
import {
  CONFIGURATOR_DATA,
  CONFIGURATOR_MODEL_TYPE,
  MODEL_PART_TYPE,
} from "../configurator/ConfiguratorData";
import { extractDiffMap } from "../util/ArrayUtil";
import { alignJoints, getJointDataFromModelData } from "../util/JointUtil";
import {
  resetTransform,
  restoreTransform,
  saveTransform,
} from "../util/ThreeUtil";
import { SugarModel } from "./SugarModel";
import { SugarModelJoint } from "./SugarModelJoint";
import { logger } from "../util/Logger";

var progress = 0;
export class SugarModelGroup extends SugarModel {
  private _partConfig: Map<string, MODEL_PART_TYPE> = new Map();
  private _configData?: CONFIGURATOR_MODEL_TYPE;

  constructor(baseModelData: any, configData?: CONFIGURATOR_MODEL_TYPE) {
    super(baseModelData);
    this._configData = configData;
  }

  async load(
    data: {
      data: any;
      partConfig: Map<string, MODEL_PART_TYPE>;
    },
    options?: {
      forceSync?: boolean;
      onBeforeLoad?: (product: SugarModel) => void;
      onAfterLoad?: (sugarModel: SugarModel, results: any[]) => void;
      onProgress?: (progress: ProgressEvent) => void;
    }
  ) {
    const baseModelData = this.data;
    //console.log("base load", baseModelData);
    const partConfig = extractDiffMap(
      this._partConfig,
      data.partConfig || new Map()
    );
    this._partConfig = data.partConfig || new Map();

    const modelJointsToLoad = Array.from(partConfig.values())
      .flatMap((partOption: MODEL_PART_TYPE) =>
        getJointDataFromModelData(partOption, baseModelData, this._configData)
      );

    const [baseModel, tailModelsJoints] = await Promise.all([
      super.load(baseModelData, {
        forceSync: true,
        onProgress: (e: ProgressEvent) => {
          //console.log("base", e.total, e.loaded);
        },
      }),

      Promise.all(
        modelJointsToLoad.map(async (mjt: any) => {
          return {
            tailModel: await new SugarModel({}).load(mjt.tailModelData, {
              forceSync: true,
              onProgress: (e: ProgressEvent) => {
                //console.log("tail", mjt.tailModelData.id, e.total, e.loaded);
                progress += e.loaded / e.total;
              },
            }),
            baseJoint: mjt.baseJoint,
          };
        })
      ),
    ]);

    if (!baseModel) {
      logger.warn("Base Model Not Loaded");
      return;
    }

    tailModelsJoints.forEach((modelJoint) => {
      const { baseJoint, tailJoint } = this._getCoupleJointsFromModelJoint(
        baseModel,
        modelJoint
      );
      this._bindJoints(baseJoint!, tailJoint!);
      baseJoint?.resetVisibility();
    });

    this.retransform();

   
    return this;
  }

  async loadJoint(
    emptyjoint: {
      joint: SugarModelJoint;
      data: { name: string; type: string; match: number | number[] };
    },
    configData?: CONFIGURATOR_MODEL_TYPE,
  ) {
    const models = (configData ?? this._configData ?? CONFIGURATOR_DATA).models;
    const data = models.find(
      (model) => model.id === emptyjoint.data.match
    );

    const tailModel = await new SugarModel(data).load(data, {
      forceSync: true,
    });
    tailModel?.position.set(0, 0, 0);

    const tailJoint = tailModel?.joints?.find(
      (j) => j.outType == emptyjoint.data.type
    );

    alignJoints(emptyjoint.joint, tailJoint!, false);
    this.root.add(tailJoint!.model);
    tailJoint!.model.freeze = true;

    return tailJoint;
  }

  retransform() {
    saveTransform(this.root);
    resetTransform(this.root);

    const box = new Box3();
    box.setFromObject(this, true);

    const size = new Vector3();
    box.getSize(size);

    const center = new Vector3();
    box.getCenter(center);

    restoreTransform(this.root);
    this.root.position.setY(size.y * 0.5 - center.y);

    const scale = new Vector3(
      this.data.x?  (this.data.x/(100 * size.x)) : 1, 
      this.data.y?  (this.data.y/(100 * size.y)) : 1, 
      this.data.z?  (this.data.z/(100 * size.z)) : 1);
    //this.scale.set(scale.x, scale.y, scale.z);
    //console.log("scale", scale);
  }

  private _getCoupleJointsFromModelJoint(
    baseModel: SugarModel,
    modelJoint: {
      tailModel?: SugarModel;
      baseJoint: any;
    }
  ) {
    const tailModel = modelJoint.tailModel as SugarModel;
    const baseJoint = baseModel?.joints?.find(
      (j) => j.jointId === modelJoint.baseJoint.id
    );

    if (!baseJoint) {
      logger.warn("Base Joint not found", modelJoint.baseJoint.id);
      return { baseJoint: undefined, tailJoint: undefined };
    }

    const tailJoint = tailModel?.joints?.find(
      (tJoint) => tJoint.outType === baseJoint?.inType
    );

    if (!tailJoint) {
      logger.warn("Tail Joint not found any type", baseJoint?.inType);
      return { baseJoint: baseJoint, tailJoint: undefined };
    }

    return { baseJoint, tailJoint };
  }

  private _bindJoints(baseJoint: SugarModelJoint, tailJoint: SugarModelJoint) {
    tailJoint.model.freeze = false;
    baseJoint.freeze = false;
    baseJoint.bindedJoint?.model?.removeFromParent();

    alignJoints(baseJoint!, tailJoint!, false);
    baseJoint.bindJoint(tailJoint);
    if (tailJoint?.model) {
      baseJoint.model?.root.add(tailJoint.model);
      tailJoint.model.freeze = true;
      tailJoint.freeze = true;
      baseJoint.freeze = true;
    }
  }
}
